import { createElement, type ComponentProps, type JSX } from "react";
import Markdown, { type Components, type ExtraProps } from "react-markdown";
import { cn } from "@/shared/utils/cn";

type Tag = keyof JSX.IntrinsicElements;

function withoutNode<P extends ExtraProps>(props: P): Omit<P, "node"> {
  const rest = { ...props };
  delete rest.node;
  return rest;
}

function styled<T extends Tag>(Element: T, className: string) {
  return (props: ComponentProps<T> & ExtraProps) => createElement(Element, { className, ...withoutNode(props) });
}

const components: Components = {
  // The page header already shows the document title.
  h1: () => null,
  h2: styled("h2", "pt-3 text-[21px] font-bold leading-tight text-foreground [overflow-wrap:anywhere]"),
  h3: styled("h3", "pt-1 text-[15px] font-semibold leading-snug text-foreground"),
  p: styled("p", "text-sm leading-relaxed text-muted-foreground"),
  ul: styled("ul", "list-disc space-y-2 pl-5 text-sm leading-relaxed text-muted-foreground"),
  ol: styled("ol", "list-decimal space-y-2 pl-5 text-sm leading-relaxed text-muted-foreground"),
  li: styled("li", "[&>ul]:mt-2"),
  strong: styled("strong", "font-semibold text-foreground"),
  pre: styled(
    "pre",
    "max-h-[420px] overflow-auto whitespace-pre-wrap rounded-[14px] border border-border bg-muted/50 p-3 font-mono text-[11px] leading-relaxed text-muted-foreground [overflow-wrap:anywhere]",
  ),
  code: styled("code", "font-mono"),
  a: (props) => {
    const external = props.href?.startsWith("http");
    return (
      <a
        className="font-medium text-primary underline-offset-4 [overflow-wrap:anywhere] hover:underline"
        {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
        {...withoutNode(props)}
      />
    );
  },
};

interface MarkdownDocumentProps {
  content: string;
  className?: string;
}

export function MarkdownDocument({ content, className }: MarkdownDocumentProps) {
  return (
    <article className={cn("space-y-4", className)}>
      <Markdown components={components}>{content}</Markdown>
    </article>
  );
}
